import routes from 'sitewide-js/core_pre_webpack/routes';
import { fetchWithCsrf } from 'js/utils';
import { BulkFollowList } from 'js/admin_content/social/BulkFollowLists';
import { FollowableUser } from '../shared/types';
import { memoize } from 'lodash';

export type BulkFollowListPayload = {
  lists: BulkFollowList[];
};

export type BulkFollowListForWork = {
  topic: {
    name: string;
    score: number;
  };
  users: FollowableUser[];
};

export async function createUserFollowing(
  userId: number,
  source: string,
  detail: { [key: string]: string | number | undefined } = {},
) {
  const response = await fetchWithCsrf(
    routes.api_v0_user_following_index_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        source,
        source_details: detail,
      }),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to create user following');
  }
}

export async function deleteUserFollowing(userId: number) {
  const response = await fetchWithCsrf(
    routes.api_v0_user_following_index_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
    { method: 'DELETE' },
  );
  if (!response.ok) {
    throw new Error('Failed to delete user following');
  }
}

export async function createUserBlock(userId: number) {
  const response = await fetchWithCsrf(
    routes.api_v0_user_blocks_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
    {
      method: 'POST',
    },
  );
  if (!response.ok) {
    throw new Error('Failed to create user block');
  }
}

export const getTopicBulkFollowList = async (
  topic: string,
): Promise<BulkFollowListPayload> => {
  const response = await fetchWithCsrf(
    routes.api_v0_social_bulk_follows_bulk_follow_list_path({
      subdomain_param: 'api',
      main_topic: topic,
    }),
    {
      method: 'GET',
    },
  );
  if (response.status === 404) {
    window.alert('Research interest not found.');
  } else if (!response.ok) {
    throw new Error('Failed to get bulk follow list');
  }

  return await response.json();
};

export const updateBulkFollowList = async (
  topic: string,
  userIds: number[],
) => {
  const response = await fetchWithCsrf(
    routes.api_v0_social_bulk_follows_update_ri_users_path({
      subdomain_param: 'api',
    }),
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        topic,
        user_ids: userIds,
      }),
    },
  );
  if (!response.ok) {
    throw new Error('Failed to remove user from bulk follow list');
  }
};

export const getCitationCountForUser = async (
  userId: number,
): Promise<{ citationCount: number }> => {
  const response = await fetchWithCsrf(
    routes.api_v0_social_bulk_follows_citation_count_path({
      subdomain_param: 'api',
      user_id: userId,
    }),
    {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
    },
  );

  return await response.json();
};

export const getBulkFollowListForWork = memoize(
  async (workId: number): Promise<BulkFollowListForWork> => {
    const response = await fetchWithCsrf(
      routes.api_v0_social_bulk_follows_bulk_follow_list_for_work_path({
        subdomain_param: 'api',
        work_id: workId,
      }),
      {
        method: 'GET',
      },
    );
    if (!response.ok) {
      throw new Error('Failed to get bulk follow list for work');
    }
    return await response.json();
  },
);
